import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, find, first, capitalize, size, isNumber, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups, getRollupSummaryLabel } from "helpers/rollups"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { getSelected } from "modules/analyze-concentration-details/selectors"
import { getViewType, getPortfolioName, getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { numberFormatter, DEFAULT_CURRENCY } from "helpers/formatters/numbers"
import { contributorsSummaryTextElem, combineDuplicateContributors, getDollarExposure } from "helpers/contributors"
import { computeSelectedGroup } from "helpers/analyze"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getContributorsDataRaw = state => get(state, "global.data.concentrationContributors.data.model.simulation.composition.0.positions")
const getBasketContributorsDataRaw = state => get(state, "global.data.basketContributors.data.model.simulation.composition.0.positions")
const getGroupedContributorsData = state => get(state, "global.data.groupedConcentrationContributors.data.model.simulation.composition.0.compositionBy")
const getGroupedBasketContributorsData = state => get(state, "global.data.groupedBasketContributors.data.model.simulation.composition.0.compositionBy")
const getRawSelectedGroup = (_, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getRawSelectedBasketGroup = (_, props) => get(props, "selectedBasketGroup", EMPTY_OBJECT)
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getOrder = state => get(state, "modules.analyzeConcentrationDetails.order", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate
const getRawContributors = state => get(state, "global.data.concentrationContributors.data.model.simulation.composition.0")
const getRawBasketContributors = state => get(state, "global.data.basketContributors.data.model.simulation.composition.0")
const getContributorsCurrency = state => get(state, "global.data.concentrationContributors.data.model.simulation.currency")
const getBasketContributorsCurrency = state => get(state, "global.data.basketContributors.data.model.simulation.currency")
const getGroupedContributorsCurrency = state => get(state, "global.data.groupedConcentrationContributors.data.model.simulation.currency")
const getGroupedBasketContributorsCurrency = state => get(state, "global.data.groupedBasketContributors.data.model.simulation.currency")

export const getSelectedBasket = (state) => get(state, "modules.analyzeConcentrationDetails.selectedBasket")

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getSelectedBasket, getRawSelectedBasketGroup, getGroupedContributorsData, getGroupedBasketContributorsData],
  (selectedGroup, selectedBasket, selectedBasketGroup, groupedContributorsData, basketContributorsData) => {
    let group, contributorsData

    if (!selectedGroup && !selectedBasketGroup) {
      return null
    }

    if (selectedBasket && !selectedBasketGroup) {
      return null
    }

    if (selectedBasket) {
      group = selectedBasketGroup
      contributorsData = basketContributorsData
    }

    if (!group && selectedGroup) {
      group = selectedGroup
      contributorsData = groupedContributorsData
    }

    const result = computeSelectedGroup(group, contributorsData)
    if (result) {
      result.totalPercentEquity /= 100
    }

    return result
  },
)

const getContributorsEquity = createSelector(
  [getRawContributors],
  (contributors) => {
    const { referenceEquity, gmv } = contributors
    return referenceEquity || gmv
  },
)

const getBasketContributorsEquity = createSelector(
  [getRawBasketContributors],
  (basketContributors) => get(basketContributors, "gmv"),
)

const getContributorsDisparity = createSelector(
  [getRawContributors],
  (contributors) => {
    const { positionsCount, positions } = contributors
    return positionsCount !== size(positions)
  },
)

// This normally would be a getSelectedField selector but in this case,
// it never changes and you cant actually get the right key from the items(getItems)
const SELECTED_FIELD = "size"

const getUniqueContributors = createSelector(
  [getContributorsDataRaw],
  (contributors) => combineDuplicateContributors(contributors, true),
)

const getContributorsData = createSelector(
  [getUniqueContributors, getSelectedBasket, getBasketContributorsDataRaw],
  (contributors, selectedBasket, basketContributors) => {
    return selectedBasket ? basketContributors : contributors
  },
)

const getFlatDollarEquity = createSelector(
  [getSelectedBasket, getContributorsEquity, getBasketContributorsEquity],
  (selectedBasket, portfolioEquity, basketEquity) => selectedBasket ? basketEquity : portfolioEquity,
)

const getContributors = createSelector(
  [getContributorsData, getContributorsEquity, getFlatDollarEquity],
  (data, equity, dollarEquity) => map(data, security => {
    const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
    return {
      id: security.id,
      modelProviderId: security.modelProviderId,
      country: security.country,
      description: security.description,
      classification: get(security, "classification.name"),
      currency: security.currency,
      assetClass: security.assetClass,
      assetSubclass: security.assetSubclass,
      isUnmodeled: security.isUnmodeled,
      size: security.economicExposure / 100,
      economicExposure: getDollarExposure(security.economicExposure, dollarEquity),
      bloombergTicker: security.bloombergTicker,
      factors: {
        maxConcentration: numberFormatter(security.economicExposure * equity),
      },
      ticker,
      exchange,
    }
  }),
)

const getFilteredContributors = createSelector(
  [getContributors],
  (data) => filter(data, security => get(security, SELECTED_FIELD) != null),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getOrder],
  (data, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "economicExposure":
            return order.abs ? Math.abs(security.size) : security.size
          default: {
            const value = get(security, SELECTED_FIELD)
            return order.abs && isNumber(value) ? Math.abs(value) : value
          }
        }
      },
    ], [order.dir])
  },
)

const groupContributors = ({ data, equity }) => map(data, group => ({
  ...group,
  contributors: map(combineDuplicateContributors(group.contributors), security => {
    const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
    return {
      ...security,
      isUnmodeled: security.isUnmodeled,
      size: security.economicExposure / 100,
      ticker,
      exchange,
      economicExposure: getDollarExposure(security.economicExposure, equity),
    }
  }),
  name: group.description,
  totalPercentEquity: group.totalPercentEquity / 100,
  economicExposure: getDollarExposure(group.totalPercentEquity, equity),
}))

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getContributorsEquity],
  (data, equity) => groupContributors({ data, equity }),
)

const getGroupedBasketContributors = createSelector(
  [getGroupedBasketContributorsData, getBasketContributorsEquity, getSelectedBasket],
  (groups, equity, selectedBasket) => {
    if (groups && selectedBasket) {
      return groupContributors({ data: groups, equity })
    }

    return groupContributors({ groups, equity })
  },
)

const getFilteredGroupedContributors = createSelector(
  [getSelectedBasket, getGroupedBasketContributors, getSelectedGroup, getGroupedContributors],
  (selectedBasket, basketContributors, selectedGroup, contributors) => {
    return filter(selectedBasket ? basketContributors : contributors,
      group => !selectedGroup || selectedGroup.id === group.id)
  })

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getOrder],
  (data, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, SELECTED_FIELD),
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "economicExposure":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, SELECTED_FIELD)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy([
        (group) => {
          switch (order.by) {
            case "name":
              return group.description
            default:
              return order.abs ? Math.abs(group.totalPercentEquity) : group.totalPercentEquity
          }
        },
      ], [order.dir])
      .value()
  },
)

export const getAvailableRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories, getSelectedDate, getSelectedBasket],
  (tagCategories, classifications, attributeCategories, date, selectedBasket) => {
    const accountRollups = {
      classifications,
      tagCategories: selectedBasket ? null : tagCategories,
      attributeCategories: selectedBasket ? null : attributeCategories,
    }
    return getAllAvailableRollups(accountRollups, date)
  },
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getSelectedBasket, getBasketContributorsDataRaw, getContributorsEquity, getRawContributors, getContributorsDisparity, getCurrency],
  (contributorsGrouping, contributors, groups, selectedGroup, selectedBasket, basketContributors, equity, rawComposition, positionsDisparity, currency) => contributorsSummaryTextElem({
    contributorsGrouping,
    contributors,
    groups,
    selectedGroup,
    selectedBasket,
    basketContributors,
    equity,
    ...pick(rawComposition, ["referenceEquity", "gmv"]),
    positionsDisparity,
    currency,
  }))

const getCsvCurrencyCode = createSelector(
  [getSelectedBasket, getContributorsGrouping, getSelectedGroup, getContributorsCurrency, getBasketContributorsCurrency, getGroupedContributorsCurrency, getGroupedBasketContributorsCurrency],
  (selectedBasket, contributorsGrouping, selectedGroup, contributorsCurrency, basketCurrency, groupedCurrency, groupedBasketCurrency) => {
    const isGrouped = Boolean(contributorsGrouping || selectedGroup)
    const responseCurrency = selectedBasket
      ? (isGrouped ? groupedBasketCurrency : basketCurrency)
      : (isGrouped ? groupedCurrency : contributorsCurrency)
    return responseCurrency || DEFAULT_CURRENCY
  },
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup, getCsvCurrencyCode],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, currency) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return {
        ...computeGroupedContributorsCSV({
          type: "Economic Exposure",
          portfolioName,
          selected: null,
          selectedDate,
          viewType,
          group,
          net: true,
          showEconomicExposure: true,
          currency,
        }),
        fileName: `${portfolioName} ${capitalize(viewType)} Concentration Grouped By ${group.name} ${selectedDate}`,
      }
    }

    if (contributorsGrouping) {
      return {
        ...computeContributorsGroupCSV({
          type: "Economic Exposure",
          portfolioName,
          selected: null,
          selectedDate,
          viewType,
          contributorsGrouping,
          data: groupedData,
          net: true,
          showEconomicExposure: true,
          currency,
        }),
        fileName: `${portfolioName} ${capitalize(viewType)} Concentration Grouped By ${getRollupSummaryLabel(contributorsGrouping)} ${selectedDate}`,
      }
    }

    return {
      ...computeContributorsCSV({
        type: "Economic Exposure",
        portfolioName,
        selected: null,
        selectedDate,
        viewType,
        data,
        net: true,
        showEconomicExposure: true,
        currency,
      }),
      fileName: `${portfolioName} ${capitalize(viewType)} Concentration ${selectedDate}`,
    }
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getSelectedBasket, getGroupedContributors, getGroupedBasketContributors],
  (selectedGroup, selectedBasket, groupedContributors, groupedBasketContributors) => {
    if (selectedGroup) {
      const group = find(selectedBasket ? groupedBasketContributors : groupedContributors, { id: selectedGroup.id })
      return get(group, SELECTED_FIELD)
    }

    return null
  },
)

export const hasReferenceEquity = createSelector([getRawContributors],
  (contributors) => get(contributors, "referenceEquity", null) !== null,
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors],
  (selectedGroup, groupedContributors) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
